import { z } from "zod";
import { useId, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@astryxdesign/core/Button";
import { TextInput } from "@astryxdesign/core/TextInput";
import * as stylex from "@stylexjs/stylex";
import { accessAPI, errorMessage, queries } from "../lib/api";
import { ruleSchema, type Rule, type Server } from "../lib/schemas";
import { PageHeader, SectionTabs, Drawer, ConfirmAction, FormGuard } from "../components/Workspace";
import { RequestFailure } from "../components/RequestFailure";
import { UserAccess } from "../components/UserAccess";
import { cloudQuery } from "../lib/cloud";
import { fieldError, identifierHelp, focusInvalidField } from "../lib/form-errors";
import { styles } from "../lib/styles";

export const Route = createFileRoute("/rules")({
  validateSearch: z.object({ view: z.enum(["groups", "individual"]).catch("groups") }),
  component: RulesPage,
});
function RulesPage() {
  const { view: tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [editing, setEditing] = useState<Rule | null>(null);
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState("");
  const me = useQuery(queries.me());
  const cloud = useQuery({
    ...cloudQuery(),
    enabled: tab === "individual" && me.data?.admin === true,
  });
  const state = useQuery({ ...queries.state(), enabled: me.data?.admin === true });
  const inventory = useQuery({ ...queries.inventory(), enabled: me.data?.admin === true });
  const cache = useQueryClient();
  const toggle = useMutation({
    mutationFn: accessAPI.updateRule,
    onSuccess: async (rule) => {
      await cache.invalidateQueries({ queryKey: ["access"] });
      setNotice(
        `${rule.group_name} rule ${rule.enabled ? "enabled" : "disabled"}. Synchronization must complete before the change is effective.`,
      );
    },
  });
  const close = () => {
    setAdding(false);
    setEditing(null);
  };
  if (me.isPending) return <p aria-busy="true">Checking access permissions…</p>;
  if (!me.data?.admin)
    return (
      <p {...stylex.props(styles.notice)}>
        Administrator access is required to manage access rules.
      </p>
    );
  return (
    <>
      <PageHeader
        title="Access"
        description="Control which people and groups can reach each server account."
        action={
          tab === "groups" && (
            <Button
              label="Add group rule"
              variant="primary"
              isDisabled={!state.data || !inventory.data || !!state.error || !!inventory.error}
              onClick={() => {
                setNotice("");
                setAdding(true);
              }}
            />
          )
        }
      />
      <SectionTabs
        value={tab}
        onChange={(view) => {
          void navigate({ search: { view } });
        }}
        tabs={[
          { id: "groups", label: "Group rules", count: state.data?.rules.length },
          { id: "individual", label: "Individual grants" },
        ]}
      />
      {notice && (
        <output {...stylex.props(styles.notice)}>
          {notice} <Link to="/servers">Review synchronization</Link>
        </output>
      )}
      {tab === "individual" && (
        <>
          {cloud.isPending && <p aria-busy="true">Loading individual grants…</p>}
          <RequestFailure
            error={cloud.error}
            retry={() => {
              void cloud.refetch();
            }}
            pending={cloud.isFetching}
          />
          {cloud.data &&
            (me.data.subject === cloud.data.owner_subject ? (
              <UserAccess state={cloud.data} />
            ) : (
              <p {...stylex.props(styles.notice)}>
                Only the configured owner can manage individual cloud grants.
              </p>
            ))}
        </>
      )}
      {tab === "groups" && (
        <>
          <RequestFailure
            error={state.error || inventory.error}
            retry={() => {
              void state.refetch();
              void inventory.refetch();
            }}
            pending={state.isFetching || inventory.isFetching}
          />
          {toggle.error && (
            <p role="alert" {...stylex.props(styles.error)}>
              {errorMessage(toggle.error)}
            </p>
          )}
          <section data-workspace-panel {...stylex.props(styles.panel)}>
            <h2 {...stylex.props(styles.panelHeading)}>Current mappings</h2>
            {state.isPending && (
              <output aria-busy="true" {...stylex.props(styles.notice)}>
                Loading group rules…
              </output>
            )}
            {state.data?.rules.length === 0 && (
              <p {...stylex.props(styles.empty)}>
                No mappings. Create a NetBird group whose name starts with SSH-, then add a group
                rule here.
              </p>
            )}
            {state.data?.rules.map((rule) => {
              const server = state.data.servers.find((item) => item.id === rule.server_id);
              const target = `${rule.group_name} on ${server?.alias ?? rule.server_id}`;
              return (
                <div key={rule.id} className="list-row">
                  <div>
                    <strong>{rule.group_name}</strong>
                    <p {...stylex.props(styles.small)}>
                      {server?.alias ?? rule.server_id} / {server?.login}
                    </p>
                    <p {...stylex.props(styles.small)}>Rule ID: {rule.id}</p>
                  </div>
                  <div {...stylex.props(styles.nav)}>
                    <span {...stylex.props(styles.status)}>
                      {rule.enabled ? "Enabled" : "Disabled"}
                    </span>
                    <Button
                      label="Edit rule"
                      aria-label={`Edit ${target}`}
                      size="sm"
                      onClick={() => {
                        setNotice("");
                        setEditing(rule);
                      }}
                    />
                    {rule.enabled ? (
                      <ConfirmAction
                        label="Disable rule"
                        target={target}
                        description="This group's managed SSH grant will be removed after synchronization. Existing managed sessions can close. Other grants and recovery SSH are preserved."
                        busy={toggle.isPending}
                        disabled={!!state.error}
                        onConfirm={() => toggle.mutate({ ...rule, enabled: false })}
                      />
                    ) : (
                      <Button
                        label="Enable rule"
                        aria-label={`Enable rule ${target}`}
                        size="sm"
                        isLoading={toggle.isPending && toggle.variables?.id === rule.id}
                        onClick={() => toggle.mutate({ ...rule, enabled: true })}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </section>
          {(adding || editing) && (
            <Drawer
              title={editing ? "Edit group rule" : "Add group rule"}
              description="Choose a designated NetBird group and a server account."
              onClose={close}
            >
              <RuleEditor
                key={editing?.id ?? "new"}
                rule={editing ?? undefined}
                servers={state.data?.servers ?? []}
                groups={
                  inventory.data?.groups.filter((group) => group.name.startsWith("SSH-")) ?? []
                }
                onSaved={(rule) => {
                  setNotice(
                    `${rule.group_name} rule ${editing ? "updated" : "created"}. Synchronization must complete before access changes become effective.`,
                  );
                  close();
                }}
              />
            </Drawer>
          )}
        </>
      )}
    </>
  );
}

function RuleEditor({
  rule,
  servers,
  groups,
  onSaved,
}: {
  rule?: Rule;
  servers: Server[];
  groups: { id: string; name: string }[];
  onSaved: (rule: Rule) => void;
}) {
  const element = useRef<HTMLFormElement>(null);
  const serverErrorId = useId();
  const groupErrorId = useId();
  const cache = useQueryClient();
  const [submissionError, setSubmissionError] = useState("");
  const save = useMutation({
    mutationFn: rule ? accessAPI.updateRule : accessAPI.createRule,
    onSuccess: () => cache.invalidateQueries({ queryKey: ["access"] }),
  });
  const form = useForm({
    defaultValues: {
      id: rule?.id ?? "",
      server_id: rule?.server_id ?? "",
      group_id: rule?.group_id ?? "",
    },
    validators: { onSubmit: ruleSchema.pick({ id: true, server_id: true, group_id: true }) },
    onSubmit: async ({ value }) => {
      const group = groups.find((item) => item.id === value.group_id);
      setSubmissionError("");
      if (!group) {
        setSubmissionError(
          "That group is no longer available. Choose a designated NetBird SSH- group and save again.",
        );
        return;
      }
      try {
        const saved = await save.mutateAsync(
          ruleSchema.parse({
            ...value,
            group_name: group?.name ?? "",
            enabled: rule?.enabled ?? true,
          }),
        );
        form.reset(value);
        onSaved(saved);
      } catch {
        // Preserve values and report API errors inside the active modal.
      }
    },
  });
  return (
    <section data-workspace-panel {...stylex.props(styles.panel)}>
      <h2 {...stylex.props(styles.panelHeading)}>Rule details</h2>
      <form
        ref={element}
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit().then(() => {
            if (!form.state.isValid)
              requestAnimationFrame(() => focusInvalidField(element.current));
          });
        }}
      >
        <form.Subscribe selector={(state) => state.isDirty}>
          {(dirty) => <FormGuard dirty={dirty} busy={save.isPending} />}
        </form.Subscribe>
        <div {...stylex.props(styles.form)}>
          <form.Field name="id">
            {(field) => (
              <TextInput
                controlXstyle={styles.inputControl}
                label="Rule ID"
                description={identifierHelp}
                value={field.state.value}
                onChange={field.handleChange}
                placeholder="mmrs-engineers"
                isDisabled={!!rule}
                isRequired
                status={
                  field.state.meta.errors.length
                    ? {
                        type: "error",
                        message: fieldError(field.state.meta.errors, identifierHelp)!,
                      }
                    : undefined
                }
              />
            )}
          </form.Field>
          <form.Field name="server_id">
            {(field) => (
              <label {...stylex.props(styles.field)}>
                Server account
                <select
                  {...stylex.props(styles.select)}
                  value={field.state.value}
                  onChange={(event) => field.handleChange(event.target.value)}
                  required
                  aria-invalid={field.state.meta.errors.length > 0}
                  aria-describedby={field.state.meta.errors.length ? serverErrorId : undefined}
                >
                  <option value="">Choose a server</option>
                  {servers.map((server) => (
                    <option key={server.id} value={server.id}>
                      {server.alias} / {server.login}
                    </option>
                  ))}
                </select>
                {field.state.meta.errors.length > 0 && (
                  <span id={serverErrorId} role="alert">
                    Choose the server account this group may access.
                  </span>
                )}
              </label>
            )}
          </form.Field>
          <form.Field name="group_id">
            {(field) => (
              <label {...stylex.props(styles.field)}>
                NetBird access group
                <select
                  {...stylex.props(styles.select)}
                  value={field.state.value}
                  onChange={(event) => field.handleChange(event.target.value)}
                  required
                  aria-invalid={field.state.meta.errors.length > 0}
                  aria-describedby={field.state.meta.errors.length ? groupErrorId : undefined}
                >
                  <option value="">Choose an SSH- group</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                {field.state.meta.errors.length > 0 && (
                  <span id={groupErrorId} role="alert">
                    Choose a designated NetBird SSH- group.
                  </span>
                )}
              </label>
            )}
          </form.Field>
        </div>
        <form.Subscribe selector={(state) => state.errors.length > 0}>
          {(invalid) =>
            invalid && (
              <p role="alert" {...stylex.props(styles.error)}>
                Correct the highlighted fields before saving. Your entries have been kept.
              </p>
            )
          }
        </form.Subscribe>
        {(save.error || submissionError) && (
          <p role="alert" {...stylex.props(styles.error)}>
            {submissionError || errorMessage(save.error)} Your entries have been kept. Correct the
            issue or retry Save mapping.
          </p>
        )}
        <div {...stylex.props(styles.actions)}>
          <Button
            label="Save mapping"
            type="submit"
            variant="primary"
            isLoading={save.isPending}
            isDisabled={!groups.length || !servers.length}
          />
        </div>
        {(!groups.length || !servers.length) && (
          <p {...stylex.props(styles.notice)}>
            Add a server and a designated NetBird SSH- group before creating a mapping.
          </p>
        )}
      </form>
    </section>
  );
}
